/** NPC source window adapted from the existing PC twin; all rule kernels stay shared. */
import type { ItemFieldRow } from './item-field-reader';
import type { FromBaseRow } from './from-base-context';
type FromBasePlayerInput = FromBaseRow;
import { fromBaseRecord, fromBaseUuid } from './from-base-context';
import type { FromBasePlayerIntegerMetadata } from './from-base-player-integers';
import { createFromBaseNpcSourceTypeReader } from './from-base-npc-stages';
import { constructNpcSkillSourcesFromBase } from './from-base-npc-skill-sources';
import { constructNpcThroughTiersFromBase } from './from-base-npc-stages';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createAdvDisSourceReader } from './adv-dis-source-reader';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import { readItemScalarLayerSlot } from './layer-cell-readers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readCreatureStatuses } from './creature-status-reader';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { storeCreatureSkillEntry } from './creature-skill-entry-storage';
import type { CreatureSkillBreakdownLine } from '../types';
import { stampNpcSkillSignatures } from './from-base-npc-signatures';

/** The complete skill ENTRY window. Natural features and derived skill abilities
 * are deliberately not certified by this projection of build_caster_skills. */
export function constructNpcFullSkillEntriesFromBase(base: FromBasePlayerInput, metadata: import('./from-base-player-resources').FromBasePlayerResourceMetadata,
    input: FromBaseRow, forced: Readonly<Record<string, number | null>>) {
    const { readers, definition, common, deps } = createFromBaseNpcGrantContext(base, metadata, input, 'integer-rollup-full-skills');
    const conditions = createAbilityConditionEvaluator(deps);
    const skillSources = constructNpcSkillSourcesFromBase(base, metadata, input);
    const host = constructNpcThroughTiersFromBase(base, metadata as import('./from-base-player-resources').FromBasePlayerResourceMetadata, metadata.inventoryMetadata!, input, conditions);
    const computed = host.computed as FromBaseRow, creature = computed.creature as FromBaseRow;
    const tiers = creature.skills as FromBaseRow;
    const statusIds = new Set(Object.values(readCreatureStatuses(host, 'target', definition)).filter(fromBaseRecord)
        .map(row => phpJsonString(readers.layerValue(row, 'status_id')).toUpperCase()));
    const cleaner = createComputeSourceCleaner({ ...common,
        resolveItemName: id => id.toUpperCase() === phpJsonString(base.entity_id ?? readers.itemField(host, 'item_id')).toUpperCase() ? readers.itemField(host, 'item_name') ?? '' : common.resolveItemName(id),
        queryItemType: createFromBaseNpcSourceTypeReader(base, metadata, host) });
    const names = createAdvDisSourceReader({ ...common, queryDefinition: definition });
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const sourceRows = fromBaseRecord(creature.modifier_sources) ? creature.modifier_sources : {};
    const globalLines: FromBaseRow[] = [];
    for (const layer of ['skill_rolls_adv', 'skill_rolls_dis']) {
        const total = phpJsonInt(readers.layerValue(host, layer)) * (layer === 'skill_rolls_dis' ? -1 : 1);
        if (!total) continue;
        let attributed = 0, lines: FromBaseRow[] = [], statusNames: string[] = [];
        for (const row of Object.values(sourceRows[layer] ?? {}).filter(fromBaseRecord)) {
            if (readers.layerValue(row, 'category') !== 'status_effect') continue;
            const id = readers.layerValue(row, 'href_id'), amount = readers.layerValue(row, 'value');
            if (!fromBaseUuid(id) || !isPhpNumeric(amount) || phpJsonInt(amount) <= 0) continue;
            const value = phpJsonInt(amount) * (layer === 'skill_rolls_dis' ? -1 : 1);
            const label = phpJsonString(readers.layerValue(row, 'label') ?? 'Status Effect');
            attributed += value; statusNames.push(label);
            lines.push({ value, label, kind: 'adv_dis', href_id: id.toUpperCase(), global_layer: layer });
        }
        if (Math.abs(attributed) > Math.abs(total) || (attributed !== 0 && (attributed > 0) !== (total > 0))) {
            attributed = 0; lines = []; statusNames = [];
        }
        const remaining = total - attributed;
        if (remaining) {
            const labels = [...new Set(names.sources(host, layer).map(row => phpJsonString(row.name ?? ''))
                .filter(name => name !== '' && !statusNames.includes(name)))];
            const magnitude = Math.abs(remaining);
            lines.push({ value: remaining,
                label: `Global — ${remaining > 0 ? '+' : '−'}${magnitude} stack${magnitude === 1 ? '' : 's'} from ${layer}${labels.length ? ' · ' + labels.join(', ') : ''}`,
                kind: 'adv_dis', href_id: null, global_layer: layer });
        }
        globalLines.push(...lines);
    }
    const lean = (lines: FromBaseRow[]): CreatureSkillBreakdownLine[] => lines.map(line => {
        const out = { ...line }, href = out.href_id;
        if (!out.global_layer && typeof href === 'string' && href !== '') {
            if (cleaner.name(href, host) === out.label) delete out.label;
            if (cleaner.type(href, host) === out.kind) delete out.kind;
        }
        return out;
    });
    const matches = (entry: unknown, skill: FromBasePlayerIntegerMetadata['skills'][number]): boolean => {
        if (readLayerListEntryUuid(entry) === skill.item_id) return true;
        const token = typeof entry === 'string' ? entry.trim() : fromBaseRecord(entry) ? readItemScalarLayerSlot(readers.layerValue(entry, 'variable_name')) : '';
        return token !== '' && (token.toLowerCase() === skill.variable_name.toLowerCase()
            || metadata.skills.some(row => row.item_name.toLowerCase() === token.toLowerCase() && row.item_id === skill.item_id));
    };
    const prior = fromBaseRecord(input.computed) && fromBaseRecord(input.computed.creature) ? input.computed.creature.skills : null;
    const skills: FromBaseRow = fromBaseRecord(prior) ? { ...prior } : {};
    for (const skill of metadata.skills) {
        const tier = fromBaseRecord(tiers[skill.variable_name]) ? tiers[skill.variable_name] as FromBaseRow : { tier_level: 0, sources: ['default_flawed'] };
        const lines = globalLines.map(line => ({ ...line }));
        const emit = (adv: unknown[], dis: unknown[], label: string, kind: string, href: string | null): void => {
            for (const [list, value] of [[adv, 1], [dis, -1]] as const) for (const entry of list)
                if (matches(entry, skill)) lines.push({ value, label, kind, href_id: href });
        };
        const authored = skillSources.static.host;
        const list = (raw: unknown): unknown[] => raw !== null && typeof raw === 'object' ? Object.values(raw) : [];
        if (authored) emit(list(authored.adv), list(authored.dis), authored.name, 'adv_dis', authored.href);
        for (const source of [...skillSources.static.containers, ...skillSources.carrier, ...skillSources.static.inventory, ...skillSources.static.statuses])
            emit(source.adv_list, source.dis_list, source.name, source.kind, source.href);
        const modLines = skillSources.mods.filter(source => source.skill === skill.item_id)
            .map(source => ({ value: source.mod, label: source.label, kind: source.kind, href_id: source.href }));
        const level = phpJsonInt(readers.layerValue(tier, 'tier_level'));
        const stored = storeCreatureSkillEntry({ assignedLevel: level,
            sources: (entries(tier, 'sources') as string[]).filter(source => level !== 0 || source !== 'default_flawed'),
            rawSp: fromBaseRecord(readers.layerValue(tier, 'raw_sp')) ? readers.layerValue(tier, 'raw_sp') as FromBaseRow : null, itemType: skill.item_type,
            advDis: lines.reduce((sum, line) => sum + phpJsonInt(line.value), 0),
            reversibleBreakdown: lines.some(line => statusIds.has(phpJsonString(line.href_id).toUpperCase())), advDisLines: lean(lines),
            globalMod: phpJsonInt(readers.layerValue(host, 'skill_rolls_mod')), perSkillMod: modLines.reduce((sum, line) => sum + line.value, 0),
            perSkillModLines: lean(modLines), forcedNaturalMainRoll: forced[skill.variable_name] ?? null });
        if (stored) skills[skill.variable_name] = stored; else delete skills[skill.variable_name];
    }
    if (metadata.skills.some(skill => skill.item_type === 'crafting_skill' && fromBaseRecord(skills[skill.variable_name])
        && phpJsonInt(readers.layerValue(skills[skill.variable_name] as FromBaseRow, 'tier_level')) > 0)) skills._meta = { has_crafting: true };
    creature.skills = skills;
    stampNpcSkillSignatures(base, metadata, host, skillSources);
    return { row: host, skillSources };
}
